/* =========================================================================================================
   NJ LIVE CHAT (Shopify Inbox)
   - The floating chat button is hidden while the chat is closed (CSS in layout/theme.liquid).
   - Any link to "#chat" (footer "Live Chat", "Need help?" on product pages, size guide) opens the chat.
   - The chat's invisible "nudge" bubble is made non-clickable while the chat is closed (it blocked taps on mobile).
   Shopify Inbox renders <shopify-chat> (current) - older versions rendered <inbox-online-store-chat>;
   both are supported. The chat's own button is clicked programmatically, so Shopify handles the opening.
   ========================================================================================================= */
(() => {
  'use strict';

  const CHAT_LINK_SEL = 'a[href="#chat"], a[href$="/#chat"]';
  const MAX_ATTEMPTS = 40; // ~6 seconds while the chat app is still loading

  function getHost() {
    return document.querySelector('shopify-chat') || document.querySelector('inbox-online-store-chat');
  }

  function findLauncher(host) {
    const root = host && host.shadowRoot;
    if (!root) return null;

    // Current Shopify Inbox: button with part="activator", either directly or inside <shopify-agent-runtime>
    let button = root.querySelector('[part~="activator"], button.activator');
    if (!button) {
      const runtime = root.querySelector('shopify-agent-runtime');
      if (runtime && runtime.shadowRoot) {
        button = runtime.shadowRoot.querySelector('[part~="activator"], button.activator');
      }
    }

    // Legacy <inbox-online-store-chat>
    if (!button && host.tagName === 'INBOX-ONLINE-STORE-CHAT') {
      button = root.querySelector('button[aria-label]') || root.querySelector('button');
    }

    return button;
  }

  function openChat(attempt) {
    attempt = attempt || 0;
    const host = getHost();

    if (host) {
      if (host.hasAttribute('open')) return; // already open

      if (host.tagName === 'INBOX-ONLINE-STORE-CHAT') {
        host.setAttribute('is-open', 'true'); // legacy CSS shows the widget when this is set
      }

      const launcher = findLauncher(host);
      if (launcher) {
        launcher.click();
        return;
      }
    }

    if (attempt < MAX_ATTEMPTS) {
      setTimeout(() => openChat(attempt + 1), 150);
    }
  }

  // Listen in the capture phase on window (runs before any other click listener) and stop the click there.
  // Shopify Inbox closes the chat when it sees a click outside the chat window; without this, a click on a
  // "Live Chat" link would open the chat and Inbox would close it again a few milliseconds later.
  // Opening is also deferred until the click has finished, for the same reason.
  window.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest(CHAT_LINK_SEL);
    if (!link) return;

    event.preventDefault();
    event.stopPropagation();
    setTimeout(() => openChat(), 0);
  }, true);

  // Support direct links such as nellyjohansson.com/pages/contact#chat
  if (window.location.hash === '#chat') {
    window.addEventListener('load', () => openChat());
  }

  // The chat's invisible "nudge" bubble (inside its shadow DOM) still caught taps while the chat was closed, which
  // blocked links underneath it on mobile (e.g. the collapsed footer menu). Make it hidden and non-clickable while closed.
  const NUDGE_CSS = ':host(:not([open])) .activator-nudge, :host(:not([open])) [class*="nudge"] { pointer-events: none !important; visibility: hidden !important; }';

  function patchRoot(root) {
    if (!root || root.querySelector('style[data-nj-nudge]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-nj-nudge', '');
    style.textContent = NUDGE_CSS;
    root.appendChild(style);
  }

  function disableNudge(attempt) {
    attempt = attempt || 0;
    const host = getHost();
    const root = host && host.shadowRoot;

    if (root) {
      patchRoot(root);
      const runtime = root.querySelector('shopify-agent-runtime');
      if (runtime && runtime.shadowRoot) patchRoot(runtime.shadowRoot);
    }

    // Keep checking for ~20 seconds while the chat app loads / re-renders
    if (attempt < 40) setTimeout(() => disableNudge(attempt + 1), 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => disableNudge());
  } else {
    disableNudge();
  }
})();
